
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import taxTableService from '../../services/PayrollTaxTableService';

/* ------------------------------------------------------------------ */
/* Thunks                                                              */
/* ------------------------------------------------------------------ */

export const fetchTaxRulesByYear = createAsyncThunk(
  'payrollTaxTable/fetchByYear',
  async (year, { rejectWithValue }) => {
    try {
      const json = await taxTableService.GetTaxFileByYear(year);
      const rows = Array.isArray(json?.ResultSet) ? json.ResultSet : [];
      return { year, rows };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to load tax rules');
    }
  }
);

export const createTaxRule = createAsyncThunk(
  'payrollTaxTable/create',
  async (payload, { rejectWithValue }) => {
    try {
      await taxTableService.AddTaxFile(payload);
      // Re-fetch so we get the backend-assigned HTT_TABLE_NO / HTT_RULE_NO
      const json = await taxTableService.GetTaxFileByYear(payload.year);
      const rows = Array.isArray(json?.ResultSet) ? json.ResultSet : [];
      return { year: payload.year, rows };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to add tax rule');
    }
  }
);

export const approveTaxTable = createAsyncThunk(
  'payrollTaxTable/approve', 
  async ({ year, tableNo }, { rejectWithValue }) => {
    try {
      await taxTableService.ApproveTaxFile(year, tableNo);
      // Re-fetch
      const json = await taxTableService.GetTaxFileByYear(year);
      const rows = Array.isArray(json?.ResultSet) ? json.ResultSet : [];
      return { year, rows };
    } catch (err) {
      return rejectWithValue(err?.message || 'Failed to approve tax table');
    }
  }
)

/* ------------------------------------------------------------------ */
/* Slice                                                               */
/* ------------------------------------------------------------------ */

const initialState = {
  rows: [],
  loading: false,
  adding: false,
  error: null,
  loadedYear: null,
};

const payrollTaxTableSlice = createSlice({
  name: 'payrollTaxTable',
  initialState,
  reducers: {
    clearTaxRules(state) {
      state.rows = [];
      state.loadedYear = null;
      state.error = null;
    },
    clearError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- fetch ----
      .addCase(fetchTaxRulesByYear.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTaxRulesByYear.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload.rows;
        state.loadedYear = action.payload.year;
      })
      .addCase(fetchTaxRulesByYear.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load tax rules';
        state.rows = [];
      })
      // ---- create ----
      .addCase(createTaxRule.pending, (state) => {
        state.adding = true;
        state.error = null;
      })
      .addCase(createTaxRule.fulfilled, (state, action) => {
        state.adding = false;
        state.rows = action.payload.rows;
        state.loadedYear = action.payload.year;
      })
      .addCase(createTaxRule.rejected, (state, action) => {
        state.adding = false;
        state.error = action.payload || 'Failed to add tax rule';
      })
      // ---- approve ----
      .addCase(approveTaxTable.pending, (state) => {
        state.loading = true; // reusing loading state for the button spinner
        state.error = null;
      })
      .addCase(approveTaxTable.fulfilled, (state, action) => {
        state.loading = false;
        state.rows = action.payload.rows;
      })
      .addCase(approveTaxTable.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to approve tax table';
      });
  },
});

export const { clearTaxRules, clearError } = payrollTaxTableSlice.actions;
export default payrollTaxTableSlice.reducer;